/**
 * Round controller (solo + multiplayer display). Timing uses the performance.now() timebase:
 *  - tweetShownAt / pairShownAt: rAF after the element committed (documented approximation of presentation)
 *  - inputAt: the DOM event's timeStamp, normalised (normalizeEventTs)
 *  - submitAt: when the order was handed to the market; landAt: when it settled
 */
import { create } from 'zustand';
import { SIM, type Difficulty, type Mode } from '../../shared/config';
import { solToLamports } from '../../shared/curve';
import { isMajorCatalyst } from '../../shared/catalyst';
import { Rng } from '../../shared/rng';
import { firstCorrectBotLanding, kolPlan, identityMismatches, isCorrectRole, makeRound, MISMATCH_TEXT, normalizeEventTs } from '../../shared/scenario';
import { personalBests, isPbEligible } from '../../shared/progress';
import type { DeployRequest, RoundResult, RoundSpec, TokenState } from '../../shared/types';
import { useSettings } from '../store/settings';
import { toast } from '../store/toast';
import { useUi } from '../store/ui';
import { market, now, onMarket, useFeed, isMp } from './runtime';
import { beep } from './sound';
import { load, save } from './storage';

export interface Live {
  spec: RoundSpec; start: number; // sim ms of round start
  phase: 'intro' | 'live' | 'done';
  tweetShownAt: number | null; shown: Map<string, number>; // tokenId -> perf ms shown
  actions: { kind: 'buy' | 'deploy'; tokenId?: string; role?: string; inputAt: number; submitAt: number; landAt?: number; ok?: boolean; reason?: string }[];
  deploys: number; deployTokenIds: string[]; wonDev: boolean | null; devLandAt: number | null; devReason: string;
  firstActionAt: number | null; assisted: boolean; mp: boolean; roundTokenIds: Set<string>;
}

interface GameState {
  live: Live | null; result: RoundResult | null; history: RoundResult[]; ns: string;
  mode: Mode; difficulty: Difficulty; pvp: boolean; freePlay: boolean; mpResult: unknown;
  set: (p: Partial<GameState>) => void;
}
const histKey = (ns: string) => `history.${ns}`;
export const useGame = create<GameState>((set) => ({
  live: null, result: null, history: load<RoundResult[]>(histKey('anon'), []), ns: 'anon',
  mode: 'first', difficulty: 'casual', pvp: false, freePlay: false, mpResult: null,
  set: (p) => set(p),
}));
export function setHistoryNamespace(ns: string) { useGame.setState({ ns, history: load<RoundResult[]>(histKey(ns), []) }); }
export function saveHistory(h: RoundResult[]) { const ns = useGame.getState().ns; useGame.setState({ history: h }); save(histKey(ns), h.slice(-3000)); }
export const pbs = () => personalBests(useGame.getState().history);

let unsub: (() => void) | null = null;
let endTimer: number | null = null;
const perfNow = () => performance.now();


export function startRound(opts?: { spec?: RoundSpec; startSim?: number; mp?: boolean }) {
  abortRound();
  const g = useGame.getState();
  const spec = opts?.spec ?? makeRound({ mode: g.mode, difficulty: g.difficulty, pvp: g.pvp, seed: (Math.random() * 2 ** 31) | 1 });
  const start = opts?.startSim ?? now() + 1500;
  const ids = new Set<string>(); for (const e of spec.events) if (e.kind === 'launch' || e.kind === 'vampBot') ids.add(e.token.id);
  const live: Live = { spec, start, phase: 'intro', tweetShownAt: null, shown: new Map(), actions: [], deploys: 0, deployTokenIds: [], wonDev: null, devLandAt: null, devReason: '', firstActionAt: null, assisted: useSettings.getState().assistedAudio, mp: !!opts?.mp, roundTokenIds: ids };
  useGame.setState({ live, result: null });
  if (spec.mode === 'dev') { useUi.setState({ feedTab: 'both' }); if (!useSettings.getState().feedOpen) useSettings.getState().set({ feedOpen: true }); }
  if (!opts?.mp) market().scheduleEvents(spec.id, start, spec.events);
  unsub = onMarket((e) => {
    const L = useGame.getState().live; if (!L || L.spec.id !== spec.id) return;
    if (e.type === 'scenario' && e.roundId === spec.id && e.ev.kind === 'tweet') useFeed.getState().add({ ...e.ev.tweet, t: start + e.ev.t }); // market time
    if (e.type === 'deploy' && e.roundId === spec.id && e.ok && e.token) onDeployLanded(e.token, e.landAt);
  });
  // MP: the room drives tweets via the same scheduled events (room.ts schedules them on the room market)
  const timer = () => {
    const L = useGame.getState().live; if (!L || L.spec.id !== spec.id) return;
    const t = now() - start;
    if (L.phase === 'intro' && t >= 0) useGame.setState({ live: { ...L, phase: 'live' } });
    if (!L.mp) {
      if (L.firstActionAt == null && t > spec.timeoutMs) return endRound('timeout');
      if (L.firstActionAt != null && t > Math.max(spec.timeoutMs, (L.firstActionAt - start) + 20000)) return endRound('auto');
    }
    endTimer = window.setTimeout(timer, 200);
  };
  timer();
}

export function markShown(tokenId: string, at: number) { const L = useGame.getState().live; if (L && L.roundTokenIds.has(tokenId) && !L.shown.has(tokenId)) L.shown.set(tokenId, at); }
export function markTweetShown(tweetId: string, at: number) { const L = useGame.getState().live; if (L && L.tweetShownAt == null && tweetId === `tw-${L.spec.seed}`) L.tweetShownAt = at; }

function inputTime(ev?: { timeStamp: number } | null) { const n = perfNow(); return ev ? Math.min(n, normalizeEventTs(ev.timeStamp, performance.timeOrigin, n)) : n; }

/** All player buys go through here so rounds can judge them. */
export function buy(tokenId: string, solAmt: number, ev?: { timeStamp: number } | null) {
  const inputAt = inputTime(ev);
  // zero / empty amount = no order: nothing reserved, no fee, no price change
  if (!(solAmt > 0) || !Number.isFinite(solAmt)) { toast('Enter a buy amount first (quick buy is 0 SOL)', 'err'); return null; }
  const L = useGame.getState().live;
  const o = market().placeOrder({ side: 'buy', tokenId, lamports: solToLamports(solAmt), slippageBps: useSettings.getState().slippagePct * 100, roundId: L && L.roundTokenIds.has(tokenId) ? L.spec.id : undefined, latencyMs: L?.spec.netLatencyMs ?? useSettings.getState().latencyMs });
  if (o.status === 'rejected') { toast(`Buy rejected: ${o.reason}`, 'err'); beep('reject'); return o; }
  if (L && L.phase !== 'done' && L.roundTokenIds.has(tokenId)) {
    const tok = market().tokens.get(tokenId)!;
    L.actions.push({ kind: 'buy', tokenId, role: tok.role, inputAt, submitAt: perfNow() });
    if (L.firstActionAt == null) L.firstActionAt = now();
    useGame.setState({ live: { ...L } });
    if (L.mp) mpReport(L.spec.pvp ? 12000 : 1500);
  } else if (L && L.phase !== 'done' && L.spec.mode !== 'dev' && L.actions.length === 0 && L.phase === 'live') {
    // a buy on a non-round coin during a FIRST/OG round counts as a wrong buy
    L.actions.push({ kind: 'buy', tokenId, role: 'noise', inputAt, submitAt: perfNow() });
    if (L.firstActionAt == null) L.firstActionAt = now();
  }
  return o;
}
/** Sell enough of the position to recover the SOL cost basis ("sell initials"); sells all if the bag is worth less. */
export function sellInitials(tokenId: string) {
  const x = market().pnl(tokenId); if (!(x.valueLamports > 0)) return toast('No position', 'err');
  return sell(tokenId, Math.min(1, x.cost / x.valueLamports));
}
export function sell(tokenId: string, fraction: number) {
  const p = market().positions.get(tokenId); if (!p) return toast('No position', 'err');
  const avail = p.raw - p.reservedRaw; const raw = fraction >= 1 ? avail : Math.floor(avail * fraction);
  if (raw <= 0) return toast('Nothing to sell', 'err');
  const L = useGame.getState().live;
  const o = market().placeOrder({ side: 'sell', tokenId, raw, slippageBps: useSettings.getState().slippagePct * 100, roundId: L?.roundTokenIds.has(tokenId) ? L.spec.id : undefined, latencyMs: L?.spec.netLatencyMs ?? useSettings.getState().latencyMs });
  if (o.status === 'rejected') { toast(`Sell rejected: ${o.reason}`, 'err'); beep('reject'); }
  return o;
}

/** Deploy from the launcher. Returns false if validation failed (state unchanged). */
export function deploy(req: DeployRequest, ev?: { timeStamp: number } | null, onRemote?: (oid: string) => void): boolean {
  const inputAt = inputTime(ev);
  const L = useGame.getState().live;
  const inRound = !!L && L.phase === 'live' && L.spec.mode === 'dev';
  if (inRound && L!.deploys >= SIM.MAX_DEV_DEPLOYS) { toast(`Round limit: ${SIM.MAX_DEV_DEPLOYS} deploys`, 'err'); return false; }
  const r = market().deploy(req, { roundId: inRound ? L!.spec.id : undefined, latencyMs: L?.spec.netLatencyMs ?? useSettings.getState().latencyMs });
  if (!r.ok) { toast(`Deploy rejected: ${r.reason}`, 'err'); beep('reject'); return false; }
  onRemote?.(r.id);
  if (isMp() && inRound) void import('./room').then(({ send }) => send({ t: 'deploy', oid: r.id, name: req.name, ticker: req.ticker, platform: req.platform, pair: req.pair, feeMode: req.feeMode, art: { seed: req.art.seed, degraded: !!req.art.degraded }, devBuySol: req.devBuySol, inputMs: null }));
  if (inRound) {
    const mism = identityMismatches(req, L!.spec.target);
    const landRel = r.landAt - L!.start; const botRel = firstCorrectBotLanding(L!.spec);
    const ok = mism.length === 0 && landRel < botRel && !L!.wonDev;
    L!.deploys++;
    L!.actions.push({ kind: 'deploy', inputAt, submitAt: perfNow(), landAt: landRel, ok, reason: mism.length ? mism.map((m) => MISMATCH_TEXT[m]).join(', ') : landRel < botRel ? 'landed before bot deployers' : 'bot deployer landed first' });
    if (L!.firstActionAt == null) L!.firstActionAt = now();
    if (ok) { L!.wonDev = true; L!.devLandAt = landRel; } else if (L!.wonDev == null) L!.devReason = L!.actions[L!.actions.length - 1]!.reason!;
    useGame.setState({ live: { ...L! } });
    if (L!.mp) mpReport(1500);
  }
  return true;
}

function onDeployLanded(tok: TokenState, _landAt: number) {
  const L = useGame.getState().live; if (!L) return;
  L.deployTokenIds.push(tok.id); L.roundTokenIds.add(tok.id);
  const a = [...L.actions].reverse().find((x) => x.kind === 'deploy' && !x.tokenId); if (a) a.tokenId = tok.id;
  if (a?.ok) {
    // DEV win: accelerated run, only through the linked eligible catalyst (setRun refuses otherwise)
    const post = L.spec.events.find((e) => e.kind === 'tweet' && e.tweet.target);
    const postId = post && post.kind === 'tweet' && isMajorCatalyst(post.tweet) ? post.tweet.id : undefined;
    const d = L.spec.difficulty; const mul = d === 'casual' ? 8 : d === 'pro' ? 14 : 24;
    if (market().setRun(tok.id, { start: now(), peakAt: 9000, endAt: 40000, mul, kols: [] }, postId)) {
      for (const e of kolPlan(new Rng(L.spec.seed).derive('devkol'), tok.id, now() - L.start, d)) market().scheduleEvents(L.spec.id, L.start, [e]);
    }
  }
}

export function abortRound() {
  const L = useGame.getState().live;
  if (endTimer) clearTimeout(endTimer); endTimer = null;
  unsub?.(); unsub = null;
  if (L && !L.mp) market().cancelRound(L.spec.id);
  if (L) useGame.setState({ live: null });
}

export function endRound(why: 'user' | 'timeout' | 'auto' | 'mp') {
  const L = useGame.getState().live; if (!L || L.phase === 'done') return;
  const res = judge(L, why);
  if (!L.mp) market().cancelRound(L.spec.id); // stale events + pending round orders are cancelled; late fills cannot credit the next round
  if (endTimer) clearTimeout(endTimer); endTimer = null; unsub?.(); unsub = null;
  useGame.setState({ live: { ...L, phase: 'done' }, result: res });
  if (!useGame.getState().freePlay) saveHistory([...useGame.getState().history, res]);
  beep(res.ok ? 'win' : 'lose');
}

export function judge(L: Live, why: string): RoundResult {
  const s = L.spec; const m = market();
  let pnl = 0, fees = 0, entry: number | null = null;
  for (const id of L.roundTokenIds) { const p = m.positions.get(id); if (!p) continue; const x = m.pnl(id); pnl += x.realized + x.unrealized; fees += p.feesLamports; }
  const base: RoundResult = { id: `${s.id}-${Date.now().toString(36)}`, roundId: s.id, at: Date.now(), mode: s.mode, difficulty: s.difficulty, pvp: s.pvp, ok: false, verdict: 'MISS', reason: '', reactionMs: null, landMs: null, entryMcUsd: null, feesSol: fees / SIM.LAMPORTS, pnlSol: pnl / SIM.LAMPORTS, assisted: L.assisted, multiplayer: L.mp, details: { endedBy: why } };
  const tweetAt = L.tweetShownAt;
  if (s.mode === 'dev') {
    const deps = L.actions.filter((a) => a.kind === 'deploy');
    if (!deps.length) return { ...base, verdict: 'TOO SLOW', reason: 'no deploy before the timeout' };
    const win = deps.find((d) => d.ok);
    const d = win ?? deps[deps.length - 1]!;
    const react = tweetAt != null ? d.inputAt - tweetAt : null;
    const botRel = firstCorrectBotLanding(s);
    return { ...base, ok: !!win, verdict: win ? 'FIRST' : 'BEATEN', reason: d.reason ?? '', reactionMs: react, landMs: d.landAt != null ? d.landAt - s.tweetAt : null,
      details: { ...base.details, deploys: deps.length, raceWinner: win ? 'you' : 'bot deployer', botLandedMs: Number.isFinite(botRel) ? Math.round(botRel - s.tweetAt) : null, yourLandMs: d.landAt != null ? Math.round(d.landAt - s.tweetAt) : null, vamps: Math.max(0, deps.length - 1) } };
  }
  const buys = L.actions.filter((a) => a.kind === 'buy');
  const first = buys[0];
  if (!first) return { ...base, verdict: 'TOO SLOW', reason: 'no buy before the timeout' };
  const ok = isCorrectRole(s, first.role ?? '', 'first');
  const tok = first.tokenId ? m.tokens.get(first.tokenId) : undefined;
  const trade = tok?.trades.find((t) => t.kind === 'player' && t.side === 'buy'); entry = trade?.mcUsd ?? null;
  const ref = s.mode === 'og' ? tweetAt : (first.tokenId ? L.shown.get(first.tokenId) ?? null : null);
  const react = ref != null ? first.inputAt - ref : null;
  let reason = 'correct pair';
  if (!ok) {
    if (s.mode === 'og' && first.role === 'copy') reason = 'wrong original: you bought a copy';
    else if (tok && tok.role !== 'noise') { const mm = identityMismatches(tok, s.target); reason = mm.length ? mm.map((x) => MISMATCH_TEXT[x]).join(', ') : s.pvp ? 'not the first coin' : 'not the first correct pair'; }
    else reason = 'not a coin for this meme';
  }
  const res: RoundResult = { ...base, ok, verdict: ok ? 'HIT' : 'WRONG', reason, reactionMs: ok ? react : null, entryMcUsd: entry, landMs: SIM.DEFAULT_LATENCY_MS,
    details: { ...base.details, tweetShown: tweetAt != null, inputToSubmitMs: Math.round(first.submitAt - first.inputAt), wrongReaction: !ok && react != null ? Math.round(react) : null } };
  if (s.pvp) {
    const second = buys.find((b) => b.tokenId !== first.tokenId && b.role === 'vamp');
    const r2 = second?.tokenId ? L.shown.get(second.tokenId) : undefined;
    res.reaction2Ms = second && r2 != null ? second.inputAt - r2 : null;
    res.details.rotated = !!second;
  }
  return res;
}

// landing report for the player's fills: "landed at $X (impact +Y%, slipped Z% from click)"
onMarket((e) => {
  if (e.type !== 'order' || e.order.status !== 'filled' || e.order.postFillMcUsd == null || e.order.preFillMcUsd == null) return;
  const o = e.order; const impact = (o.postFillMcUsd! / o.preFillMcUsd! - 1) * 100; const slip = o.clickMcUsd ? (o.preFillMcUsd! / o.clickMcUsd - 1) * 100 : 0;
  const fmt = (n: number) => (n >= 1e6 ? `$${(n / 1e6).toFixed(2)}M` : `$${(n / 1e3).toFixed(1)}K`);
  toast(`${o.side === 'buy' ? 'Bought' : 'Sold'}: landed at ${fmt(o.preFillMcUsd!)} (impact ${impact >= 0 ? '+' : ''}${impact.toFixed(1)}%, slipped ${slip >= 0 ? '+' : ''}${slip.toFixed(1)}% from click)`, 'ok');
});

let reportTimer: number | null = null;
function mpReport(delay: number) { if (reportTimer) return; reportTimer = window.setTimeout(() => { reportTimer = null; void import('./room').then((r) => r.reportResult()); }, delay); }

export function pbFor(r: RoundResult) { const p = pbs(); return isPbEligible(r) && Object.values(p).some((x) => x.id === r.id); }
export { isMp };
